import fetchWithTimeout from '@picsart/rc/fetchWithTimeout';

import { BACKGROUNDS_URL, PA_HEADERS } from 'configs/constants';
import { ICMSBackgrounds, IShopBackgrounds } from 'types';

function addQueryParameters(url: string, parameters: Record<string, string | number> = {}) {
  const separator = /\?/.test(url) ? '&' : '?';
  const names = Object.keys(parameters);

  if (names.length === 0) return url;

  return url + separator + names.map(name => `${name}=${encodeURIComponent(parameters[name])}`).join('&');
}

class DataService {
  async fetchData(url: string, languageCode: string) {
    const response = await fetchWithTimeout(url, {
      headers: {
        method: 'GET',
        versioncode: '660',
        'language-code': languageCode,
        ...PA_HEADERS,
      },
    });

    const data = await response.json();
    if (!data.response) throw new Error('Bad data');

    return data;
  }

  async getBackgrounds(languageCode: string, queryParams?: Record<string, string | number>, url = '') {
    const urlToFetch = addQueryParameters(url || BACKGROUNDS_URL, queryParams);
    const data = await this.fetchData(urlToFetch, languageCode);
    return {
      response: data.response as IShopBackgrounds[],
      nextUrl: data.metadata?.next_page,
    };
  }

  async getCMSBackgrounds(languageCode: string): Promise<ICMSBackgrounds[]> {
    const apiEndpoint = process.env.PICSART_API_URL || 'https://api.picsart.com';
    const searchEndpoint = '/search/create-flow/photo/results';

    const queryParams = {
      formula_name: 'remix_original_other',
      enable_cloud_card: '0',
      repeat_value: '2',
      history_view_type: 'before_after_animation',
      order: 'picsart,unsplash',
      q: 'be310823',
      include_premiums: '1',
    };

    const queryString = new URLSearchParams(queryParams).toString();
    const urlToFetch = `${apiEndpoint}${searchEndpoint}?${queryString}`;

    try {
      const data = await this.fetchData(urlToFetch, languageCode);
      const backgrounds = data.response as ICMSBackgrounds[];

      const contentUrl = backgrounds[0]?.content_url;
      if (contentUrl) {
        const contentData = await this.fetchData(contentUrl, languageCode);
        if (backgrounds.length === 0) {
          backgrounds.push({
            data: contentData.response,
            size: contentData.response.length,
            source: '',
            render_type: '',
            infinite: false,
            type: '',
          });
        } else {
          backgrounds[0].data = [...backgrounds[0].data, ...contentData.response];
          backgrounds[0].size = backgrounds[0].data.length;
        }
      }

      return backgrounds || [];
    } catch (error) {
      throw new Error(`Failed to fetch CMS backgrounds: ${error}`);
    }
  }
}

export default new DataService();
